import React from 'react';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { locations } from '../mockData';
import { useToast } from '../hooks/use-toast';

const ContactSection = () => {
  const { toast } = useToast();

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "Message Sent!",
      description: "Thank you for contacting us. We'll get back to you soon."
    });
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-gray-50 via-blue-50 to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Get In Touch</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Have questions? We're here to help. Reach out to us anytime.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="text-2xl">Send Us a Message</CardTitle>
              <CardDescription>Fill out the form below and we'll respond within 24 hours</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                    <Input placeholder="John" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                    <Input placeholder="Doe" required />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <Input type="email" placeholder="your@email.com" required />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                  <Input type="tel" placeholder="+1 (123) 456-7890" />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                  <Textarea placeholder="How can we help you?" rows={5} required />
                </div>
                
                <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 text-lg">
                  Send Message
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Location Information */}
          <div className="space-y-6">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl">Our Locations</CardTitle>
                <CardDescription>Visit us at any of our branches across Canada</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {locations.map((location, index) => (
                  <div key={index} className="pb-6 border-b border-gray-200 last:border-0 last:pb-0">
                    <h4 className="font-semibold text-lg text-gray-900 mb-3">{location.city}</h4>
                    <div className="space-y-2">
                      <div className="flex items-start gap-3 text-gray-600">
                        <MapPin className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{location.address}</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-600">
                        <Phone className="w-5 h-5 text-red-600 flex-shrink-0" />
                        <span className="text-sm">{location.phone}</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-600">
                        <Mail className="w-5 h-5 text-red-600 flex-shrink-0" />
                        <span className="text-sm" dangerouslySetInnerHTML={{ __html: location.email || location.emailPlain }}></span>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Business Hours */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-6 h-6 text-blue-600" />
                  Business Hours
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-gray-700">
                  <div className="flex justify-between py-2">
                    <span className="font-medium text-lg">Open 24 Hours</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium\">Monday - Sunday:</span>
                    <span className="text-green-600 font-semibold">24/7</span>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-200 text-center">
                    <span className="text-blue-600 font-semibold">24/7 Customer Support Available Online</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;